import { schema, types } from 'papr'

export const draftSchema = schema(
	{
		// --- ALWAYS SET FIELDS

		/**
		 * ID of the author of the draft.
		 *
		 * In personal publications this is always the owner of the publication.
		 * In team publications it can be any member of the publication.
		 *
		 * In team publications editors and the owner can modify the draft even if they are not the author.
		 */
		author: types.objectId({ required: true }),
		/**
		 * Whether the draft has been published.
		 */
		isPublished: types.boolean({ required: true }),
		/**
		 * Whether the draft is active.
		 *
		 * Will be set to false when the draft is published.
		 */
		isActive: types.boolean({ required: true }),
		/**
		 * Date the draft was last updated.
		 */
		dateUpdated: types.date({ required: true }),

		// --- ONLY SET AFTER PUBLISHING

		/**
		 * Title of the published post.
		 *
		 * This is only set after the draft has been published.
		 */
		title: types.string(),
		/**
		 * HTML content of the published post.
		 *
		 * This is only set after the draft has been published.
		 */
		content: types.string(),
		/**
		 * Markdown content of the published post.
		 *
		 * This is only set after the draft has been published.
		 */
		contentMarkdown: types.string(),
		/**
		 * ID of the post that was published from this draft.
		 *
		 * This is only set after the draft has been published.
		 */
		post: types.objectId(),
		/**
		 * ID of the series the post has been published to.
		 *
		 * This is only set after the draft has been published.
		 */
		series: types.objectId(),
		/**
		 * URL of the cover image of the published post.
		 *
		 * This is only set after the draft has been published.
		 */
		coverImage: types.string(),
		/**
		 * Tags of the published post.
		 *
		 * This is only set after the draft has been published.
		 */
		tags: types.array(types.objectId()),

		// --- GITHUB AS SOURCE ONLY

		/**
		 * Whether the draft was sourced from GitHub.
		 */
		sourcedFromGithub: types.boolean(),
		/**
		 * Additional metadata about the draft sourced from GitHub.
		 */
		githubAsSourceMeta: types.objectGeneric(types.unknown()),

		// --- DEPRECATED

		/**
		 * Excerpt of the published post.
		 *
		 * This is only set after the draft has been published.
		 *
		 *  @deprecated This is not set anymore.
		 */
		brief: types.string(),
		/**
		 * URL of the original article URL.
		 *
		 * This is only set after the draft has been published.
		 *
		 *  @deprecated This is not set anymore.
		 */
		originalArticleURL: types.string(),
	},
	{
		timestamps: true,
		defaults: {
			isActive: true,
			isPublished: false,
		},
	},
)

export type DraftDocument = (typeof draftSchema)[0]

export type UnpublishedDraftDocument = Pick<DraftDocument, '_id' | 'author' | 'dateUpdated'> & {
	isActive: true
	isPublished: false
}

type DeprecatedFields = 'brief' | 'originalArticleURL'
export type PublishedDraftDocument = Omit<DraftDocument, DeprecatedFields> & {
	isActive: false
	isPublished: true
}
export type DraftSchemaOptions = (typeof draftSchema)[1]
